import React, { useCallback, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  FiArrowRight, FiGrid, FiPieChart, FiTool, FiUsers, FiShield,
} from "react-icons/fi";

import styles from "./ToolsPage.module.css";
import { STRINGS } from "./strings";
import ToolFeedback from "../toolFeedback/ToolFeedback";

const SITE = "https://www.danielrayrangel.com";

/* Card icons by key, so strings.js stays copy and never imports a component. */
const ICONS = {
  qr: FiGrid,
  split: FiPieChart,
  tool: FiTool,
  users: FiUsers,
  shield: FiShield,
};

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

/* The two live tools, described the way their own pages describe themselves.
 * These strings are lifted verbatim out of each tool's WebApplication markup
 * (QrToolPage.js, TipSplitPage.js) so the hub and the tool never tell a crawler
 * two different stories about the same app. Change one, change the other. */
const FEATURE_LISTS = {
  qr: "Wi-Fi, vCard, URL, email, SMS, WhatsApp, location, calendar QR codes; custom dot and corner shapes; custom colors; logo embedding; PNG, JPG and SVG download",
  "tip-split":
    "Tip pool split by weighted hours; tip-out percentages of tips, food sales, or liquor sales; saved roster; printable per-person breakdown; PDF download",
};
const APP_CATEGORIES = { qr: "UtilityApplication", "tip-split": "BusinessApplication" };

/* The free-tools directory.
 *
 * Two jobs at once, and the order matters: it has to be a useful index first —
 * somebody who came for the QR generator should reach it in one tap — and a
 * place to ask second. So the ask sits below the tools, not in front of them,
 * and nothing on the page gates a tool behind a form.
 *
 * The lure is the roadmap. Asking "what should I build?" into empty space gets
 * nothing; asking somebody whether they'd use a job pricing calculator, next to
 * a description of what it would do, gets an answer and a name to reach.
 */
const ToolsPage = () => {
  const t = STRINGS.en;
  const reduceMotion = useReducedMotion();

  /* One dialog, three doors.
   *
   * ToolFeedback hardcodes its title's id, so a second mounted copy would put a
   * duplicate id in the document and the dialog would lose its accessible name.
   * This page keeps exactly one and swaps what it is asking about.
   *
   * The nonce in the key is load-bearing. ToolFeedback bails out of render when
   * closed rather than unmounting, so its "sent" screen outlives a close —
   * without a fresh key, the second person to tap the same button gets a
   * thank-you note instead of a form.
   */
  const [request, setRequest] = useState(null);
  const triggerRef = useRef(null);
  const opens = useRef(0);

  const askFor = (spec) => (event) => {
    // currentTarget is only valid during the handler, so read it now: this is
    // the button ToolFeedback hands focus back to on close.
    triggerRef.current = event.currentTarget;
    opens.current += 1;
    setRequest({ ...spec, key: `${spec.id}-${opens.current}` });
  };

  const closeFeedback = useCallback(() => setRequest(null), []);

  const motionProps = {
    initial: reduceMotion ? false : "hidden",
    whileInView: "visible",
    viewport: { once: true, margin: "-80px" },
    variants: fadeUp,
  };

  return (
    <>
      <Helmet>
        <title>{t.meta.title}</title>
        <meta name="description" content={t.meta.description} />
        <link rel="canonical" href={`${SITE}/tools`} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE}/tools`} />
        <meta property="og:title" content={t.meta.ogTitle} />
        <meta property="og:description" content={t.meta.ogDescription} />
        <meta property="og:site_name" content="Exhale Software Solutions" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t.meta.ogTitle} />
        <meta name="twitter:description" content={t.meta.ogDescription} />

        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&display=swap"
          rel="stylesheet"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Free Tools for Small Business",
            url: `${SITE}/tools`,
            description: t.meta.description,
            isPartOf: {
              "@type": "WebSite",
              name: "Exhale Software Solutions",
              url: SITE,
            },
            mainEntity: {
              "@type": "ItemList",
              itemListOrder: "https://schema.org/ItemListOrderAscending",
              numberOfItems: t.tools.length,
              itemListElement: t.tools.map((tool, i) => ({
                "@type": "ListItem",
                position: i + 1,
                item: {
                  "@type": "WebApplication",
                  name: tool.name,
                  url: `${SITE}${tool.href}`,
                  applicationCategory: APP_CATEGORIES[tool.id],
                  operatingSystem: "Any (web browser)",
                  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
                  featureList: FEATURE_LISTS[tool.id],
                },
              })),
            },
            provider: {
              "@type": "Organization",
              name: "Exhale Software Solutions",
              url: SITE,
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: t.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "Free Tools", item: `${SITE}/tools` },
            ],
          })}
        </script>
      </Helmet>

      <main className={styles.page}>
        {/* ── HERO ──
            No scroll-triggered motion above the fold or on the tool cards:
            react-snap never scrolls during the build crawl, so a whileInView
            element gets frozen into the static HTML at opacity 0. The tool
            names and their links are the whole point of the page to a crawler
            and must not wait on hydration to exist. */}
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <p className={styles.eyebrow}>
              {t.hero.badges.map((badge, i) => (
                <React.Fragment key={badge}>
                  {i > 0 && <span className={styles.eyebrowDot}>✦</span>}
                  <span className={i === 0 ? styles.eyebrowMark : undefined}>{badge}</span>
                </React.Fragment>
              ))}
            </p>
            <h1 className={styles.heroTitle}>
              {t.hero.titleLine1}
              <br />
              <em>{t.hero.titleLine2}</em>
            </h1>
            <p className={styles.heroSub}>{t.hero.sub}</p>
          </div>
        </section>

        {/* ── LIVE TOOLS ── */}
        <section className={styles.tools}>
          <div className={styles.sectionHead}>
            <p className={styles.panelLabel}>{t.live.label}</p>
            <h2 className={styles.sectionTitle}>
              {t.live.titleLine1}
              <br />
              <em>{t.live.titleLine2}</em>
            </h2>
          </div>

          <div className={styles.toolGrid}>
            {t.tools.map((tool) => {
              const Icon = ICONS[tool.icon];
              return (
                /* Deliberately not a linked card: one link, at the bottom,
                   where a thumb expects it and a screen reader announces it
                   once with a name that says where it goes. */
                <article key={tool.id} className={styles.toolCard}>
                  <Icon className={styles.toolIcon} aria-hidden="true" />
                  <h3 className={styles.toolName}>{tool.name}</h3>
                  <p className={styles.toolFor}>{tool.who}</p>
                  <p className={styles.toolBody}>{tool.body}</p>
                  <p className={styles.toolMeta}>{tool.meta}</p>
                  <a href={tool.href} className={styles.toolCta}>
                    {tool.cta} <FiArrowRight aria-hidden="true" />
                  </a>
                </article>
              );
            })}
          </div>
        </section>

        {/* ── IN THE WORKS ──
            The lead surface that actually works: a described tool somebody can
            put their name against beats a blank "any suggestions?" box. */}
        <section className={styles.roadmap}>
          <motion.div className={styles.sectionHead} {...motionProps}>
            <p className={styles.panelLabel}>{t.roadmap.label}</p>
            <h2 className={styles.sectionTitle}>
              {t.roadmap.titleLine1}
              <br />
              <em>{t.roadmap.titleLine2}</em>
            </h2>
            <p className={styles.sectionSub}>{t.roadmap.sub}</p>
          </motion.div>

          <div className={styles.toolGrid}>
            {t.roadmap.items.map((item) => (
              <article key={item.id} className={styles.roadmapCard}>
                <p className={styles.badge}>{t.roadmap.badge}</p>
                <h3 className={styles.toolName}>{item.name}</h3>
                <p className={styles.toolFor}>{item.who}</p>
                <p className={styles.toolBody}>{item.body}</p>
                {/* A button, not a link: nothing here navigates, and a card that
                    isn't a destination must never look like one. */}
                <button type="button" className={styles.wantBtn} onClick={askFor(item.request)}>
                  {t.roadmap.button} <FiArrowRight aria-hidden="true" />
                </button>
              </article>
            ))}
          </div>
        </section>

        {/* ── WHY FREE ── */}
        <section className={styles.why}>
          <motion.div className={styles.sectionHead} {...motionProps}>
            <p className={styles.panelLabel}>{t.why.label}</p>
            <h2 className={styles.sectionTitle}>
              {t.why.titleLine1}
              <br />
              <em>{t.why.titleLine2}</em>
            </h2>
          </motion.div>

          <div className={styles.whyGrid}>
            {t.why.cards.map((card) => {
              const Icon = ICONS[card.icon];
              return (
                <div key={card.id} className={styles.whyCard}>
                  <Icon className={styles.whyIcon} aria-hidden="true" />
                  <h3 className={styles.whyTitle}>{card.title}</h3>
                  <p className={styles.whyBody}>{card.body}</p>
                </div>
              );
            })}
          </div>
          <p className={styles.whyNote}>{t.why.note}</p>
        </section>

        {/* ── The open ask ──
            Same quiet treatment the tip splitter gives its feedback block: it
            is a question, not a second CTA competing with the one below it. */}
        <section className={styles.feedback}>
          <div className={styles.feedbackInner}>
            <p className={styles.feedbackEyebrow}>{t.ask.eyebrow}</p>
            <p className={styles.feedbackText}>{t.ask.text}</p>
            <button
              type="button"
              className={styles.feedbackBtn}
              onClick={askFor(t.ask.request)}
            >
              {t.ask.button} <FiArrowRight aria-hidden="true" />
            </button>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className={styles.faq}>
          <div className={styles.faqInner}>
            <motion.div className={styles.sectionHead} {...motionProps}>
              <p className={styles.panelLabel}>{t.faq.label}</p>
            </motion.div>
            <div className={styles.faqList}>
              {t.faqs.map((f) => (
                <details key={f.q} className={styles.faqItem}>
                  <summary className={styles.faqQ}>{f.q}</summary>
                  <p className={styles.faqA}>{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className={styles.cta}>
          <motion.div className={styles.ctaInner} {...motionProps}>
            <p className={styles.ctaEyebrow}>
              {t.cta.eyebrow.map((word, i) => (
                <React.Fragment key={word}>
                  {i > 0 && <span className={styles.eyebrowDot}>✦</span>}
                  {word}
                </React.Fragment>
              ))}
            </p>
            <h2 className={styles.ctaTitle}>
              {t.cta.titleLine1}
              <br />
              <em>{t.cta.titleLine2}</em>
            </h2>
            <p className={styles.ctaSub}>{t.cta.sub}</p>
            <div className={styles.ctaActions}>
              <a href="/#start" className={styles.ctaPrimary}>
                {t.cta.primary}
              </a>
              <a href="/#proof" className={styles.ctaSecondary}>
                {t.cta.secondary}
              </a>
            </div>
          </motion.div>
        </section>

        <ToolFeedback
          key={request ? request.key : "closed"}
          open={Boolean(request)}
          onClose={closeFeedback}
          toolName={request ? request.toolName : ""}
          labels={request ? request.labels : t.ask.request.labels}
          returnFocusTo={triggerRef}
        />
      </main>
    </>
  );
};

export default ToolsPage;
